/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatLastActive } from "../../utils/utils";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import BioVitalsSummaryCards from "./BioVitalsSummaryCards";
import BioVitalsTrendChart from "./BioVitalsTrendChart";
import BioVitalsTypeFilter from "./BioVitalsTypeFilter";
import { readSavedFilters, usePersistFilters } from "../../hooks/filterPersistence";
import "../active-users/active-users.css";
import "./UserBioVitals.css";

const LIMIT = 20;
const STATE_KEY = "bioVitalsListState";

const VITAL_TYPES = [
  { key: 'bp', label: 'BP' },
  { key: 'sugar', label: 'Sugar' },
  { key: 'oxygen', label: 'Oxygen' },
  { key: 'salt', label: 'Salt' },
  { key: 'water', label: 'Water' },
];

// Stable CSV schema — always all five {Count, Last} pairs (029 clarification).
const EXPORT_COLUMNS = [
  { key: 'row_no', label: 'No' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'country', label: 'Country' },
  { key: 'mobile_app_version', label: 'App Version' },
  { key: 'subscription_status', label: 'Subscription' },
  { key: 'bp_count', label: 'BP Count' }, { key: 'latest_bp_time', label: 'Last BP' },
  { key: 'sugar_count', label: 'Sugar Count' }, { key: 'latest_sugar_time', label: 'Last Sugar' },
  { key: 'oxygen_count', label: 'Oxygen Count' }, { key: 'latest_oxygen_time', label: 'Last Oxygen' },
  { key: 'salt_count', label: 'Salt Count' }, { key: 'latest_salt_time', label: 'Last Salt' },
  { key: 'water_count', label: 'Water Count' }, { key: 'latest_water_time', label: 'Last Water' },
];

const emptyTotals = {
  total_bp_count: 0, total_sugar_count: 0, total_oxygen_count: 0, total_salt_count: 0, total_water_count: 0,
};

const UserBioVitalsCount = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();                     // global watch/without/all (feature 023)
  const userSegment = watchFilterValue(segment) || 'all';   // 'with_watch' | 'without_watch' | 'all'

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [users, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [splitCounts, setSplitCounts] = useState({});
  const [subscribedUserCount, setSubscribedUserCount] = useState(0);
  const [totals, setTotals] = useState(emptyTotals);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // All unticked = show every type.
  const [selectedTypes, setSelectedTypes] = useState({ bp: false, sugar: false, oxygen: false, salt: false, water: false });

  const [summary, setSummary] = useState({ current: {}, prior: {}, deltas: {} });
  const [trend, setTrend] = useState({ series: [], types: [] });

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Restore the list view on return from a drill-down (Back button); otherwise
    // default to Last 30 days (matches /user-pause-count, /user-monitor-sessions).
    const saved = readSavedFilters(STATE_KEY);
    if (saved) {
      setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
      setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
      setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
      setPreset(saved.preset || null);
      setSearchValue(saved.searchValue || '');
      setSubscriptionFilter(saved.subscriptionFilter || '');
      setSortField(saved.sortField || ''); setSortOrder(saved.sortOrder || '');
      setSelectedTypes(saved.selectedTypes || { bp: false, sugar: false, oxygen: false, salt: false, water: false });
      setPage(saved.page || 1);
    } else {
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
      setPreset('last30');
    }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, selectedTypes, subscriptionFilter);
  }, [token]);

  // Re-fetch when the global segment changes (skip initial mount).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token) return;
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, selectedTypes, subscriptionFilter);
  }, [segment]);

  // Snapshot the filters so the exact view is restored on Back (remount).
  usePersistFilters(STATE_KEY, {
    fromDate, toDate, preset, searchValue, subscriptionFilter, sortField, sortOrder, selectedTypes, page,
  });

  const typesParam = (typesObj) => Object.entries(typesObj).filter(([, v]) => v).map(([k]) => k).join(',');

  const listQs = (p, f, t, s, sf, so, typesObj, sub) => {
    let q = `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&sortField=${sf}&sortOrder=${so}&subscription_status=${sub}&user_segment=${userSegment}`;
    const tp = typesParam(typesObj);
    if (tp) q += `&types=${tp}`;
    return q;
  };
  const rangeQs = (f, t, s, typesObj, sub) => {
    let q = `from_date=${f}&to_date=${t}&search=${s}&subscription_status=${sub}&user_segment=${userSegment}`;
    const tp = typesParam(typesObj);
    if (tp) q += `&types=${tp}`;
    return q;
  };
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const applyList = (d) => {
    setData(d.data || []);
    setTotalItem(d.totalItem || 0);
    setTotalUsers(d.userCount?.[0]?.user_count || 0);
    setTotalUserBase(d.totalUserCount || 0);
    setSplitCounts(d.splitUserCounts || {});
    setSubscribedUserCount(d.subscribedUserCount || 0);
    setTotals(d.totals || emptyTotals);
  };
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, s, sf, so, typesObj, sub) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/user-bio-vitals?${listQs(p, f, t, s, sf, so, typesObj, sub)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, sf, so, typesObj, sub) => {
    setLoading(true); setError('');
    const range = rangeQs(f, t, s, typesObj, sub);
    try {
      const [list, sum, tr] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/user-bio-vitals?${listQs(p, f, t, s, sf, so, typesObj, sub)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/bio-vitals-summary?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/bio-vitals-trend?${range}`, auth()),
      ]);
      applyList(list.data);
      setSummary(sum.data.data || { current: {}, prior: {}, deltas: {} });
      setTrend(tr.data.data || { series: [], types: [] });
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  // ---- Vital-type filter ----
  const allChecked = Object.values(selectedTypes).every((v) => v);
  const hasSelection = Object.values(selectedTypes).some((v) => v);
  const showCol = (key) => !hasSelection || selectedTypes[key];

  const handleToggleType = (key) => {
    const updated = { ...selectedTypes, [key]: !selectedTypes[key] };
    setSelectedTypes(updated); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, updated, subscriptionFilter);
  };
  const handleToggleAll = () => {
    const v = !allChecked;
    const updated = { bp: v, sugar: v, oxygen: v, salt: v, water: v };
    setSelectedTypes(updated); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, updated, subscriptionFilter);
  };

  // ---- Other filters ----
  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    getList(pageNumber, fromDate, toDate, searchValue, sortField, sortOrder, selectedTypes, subscriptionFilter);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1);
    loadAll(1, fromStr || fs, toStr || ts, searchValue, sortField, sortOrder, selectedTypes, subscriptionFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, selectedTypes, subscriptionFilter); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setSearchValue(''); setSubscriptionFilter(''); setPreset(null); setPage(1);
    loadAll(1, '', '', '', sortField, sortOrder, selectedTypes, '');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, field, order, selectedTypes, subscriptionFilter);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/bio-vitals-export?from_date=${fromDate}&to_date=${toDate}&search=${searchValue}&sortField=${sortField}&sortOrder=${sortOrder}&subscription_status=${subscriptionFilter}&user_segment=${userSegment}${typesParam(selectedTypes) ? `&types=${typesParam(selectedTypes)}` : ''}`,
      auth());
    if (res.data?.success === false) throw new Error(res.data.message || 'Export failed');
    return (res.data.data?.rows || []).map((r) => ({
      ...r,
      latest_bp_time: r.latest_bp_time ? formatLastActive(r.latest_bp_time) : '',
      latest_sugar_time: r.latest_sugar_time ? formatLastActive(r.latest_sugar_time) : '',
      latest_oxygen_time: r.latest_oxygen_time ? formatLastActive(r.latest_oxygen_time) : '',
      latest_salt_time: r.latest_salt_time ? formatLastActive(r.latest_salt_time) : '',
      latest_water_time: r.latest_water_time ? formatLastActive(r.latest_water_time) : '',
    }));
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" tabIndex={0} role="button"
      onClick={() => handleSort(field)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(field); } }}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const CountLink = ({ id, type, value }) => (
    // Same-tab navigation so the browser Back button returns to this list with
    // its filters restored (filterPersistence). Opening in a new tab is avoided.
    <Link className="au-link" to={`/${type}-details/${btoa(id)}`}>{value ?? 0}</Link>
  );

  const hasRange = !!(fromDate || toDate || searchValue || subscriptionFilter);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  // Human label for the active vital types, used in the trend caption.
  const activeTypesLabel = hasSelection
    ? VITAL_TYPES.filter((t) => selectedTypes[t.key]).map((t) => t.label).join(', ')
    : 'all vital types';

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Health › <b>Bio vitals</b></span>
            <span className="au-route">/user-bio-vitals</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Bio Vitals</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the bio-vitals data. Please try again."
              onRetry={() => loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, selectedTypes, subscriptionFilter)} />
          ) : (
            <>
              {/* Summary before detail */}
              <BioVitalsSummaryCards
                totals={totals}
                splitCounts={splitCounts}
                totalUserBase={totalUserBase}
                totalUsers={totalUsers}
                subscribedUserCount={subscribedUserCount}
                deltas={summary.deltas}
              />

              <BioVitalsTrendChart data={trend.series} types={trend.types} typesLabel={activeTypesLabel} />

              {/* Vital-type filter — its own row above the table */}
              <BioVitalsTypeFilter
                types={VITAL_TYPES}
                selected={selectedTypes}
                allChecked={allChecked}
                onToggle={handleToggleType}
                onToggleAll={handleToggleAll}
              />

              {/* Filters */}
              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search by name or email" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search users" />
                    <select className="au-select" name="subscriptionFilter" value={subscriptionFilter} onChange={(e) => setSubscriptionFilter(e.target.value)} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                  </>
                )}
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="bv-range-note">
                The date range filters readings by their <b>activity date (UTC)</b> — the reading timestamp shown per user as “Last &lt;type&gt;”.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all readings (no date filter).'}
              </p>

              {/* Detail table */}
              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : users.length === 0 ? (
                <EmptyState title="No readings in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No users match the current filters.' : 'There are no bio-vitals readings to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Per-user bio vitals</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="bio-vitals.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table bv-table" data-testid="bv-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Country</th><th>App Version</th><th>Subscription</th>
                          {showCol('bp') && (<><SortTh field="bp_count" label="BP Count" /><SortTh field="latest_bp_time" label="Last BP" /></>)}
                          {showCol('sugar') && (<><SortTh field="sugar_count" label="Sugar Count" /><SortTh field="latest_sugar_time" label="Last Sugar" /></>)}
                          {showCol('oxygen') && (<><SortTh field="oxygen_count" label="Oxygen Count" /><SortTh field="latest_oxygen_time" label="Last Oxygen" /></>)}
                          {showCol('salt') && (<><SortTh field="salt_count" label="Salt Count" /><SortTh field="latest_salt_time" label="Last Salt" /></>)}
                          {showCol('water') && (<><SortTh field="water_count" label="Water Count" /><SortTh field="latest_water_time" label="Last Water" /></>)}
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={user.id}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{user.name}</td>
                            <td data-label="Email" className="au-ellipsis">{user.email}</td>
                            <td data-label="Country">{user.country}</td>
                            <td data-label="App Version">{user.mobile_app_version}</td>
                            <td data-label="Subscription">{user.subscription_status}</td>
                            {showCol('bp') && (<><td data-label="BP Count" className="tnum"><CountLink id={user.id} type="bp" value={user.bp_count} /></td><td data-label="Last BP">{formatLastActive(user.latest_bp_time)}</td></>)}
                            {showCol('sugar') && (<><td data-label="Sugar Count" className="tnum"><CountLink id={user.id} type="sugar" value={user.sugar_count} /></td><td data-label="Last Sugar">{formatLastActive(user.latest_sugar_time)}</td></>)}
                            {showCol('oxygen') && (<><td data-label="Oxygen Count" className="tnum"><CountLink id={user.id} type="oxygen" value={user.oxygen_count} /></td><td data-label="Last Oxygen">{formatLastActive(user.latest_oxygen_time)}</td></>)}
                            {showCol('salt') && (<><td data-label="Salt Count" className="tnum"><CountLink id={user.id} type="salt" value={user.salt_count} /></td><td data-label="Last Salt">{formatLastActive(user.latest_salt_time)}</td></>)}
                            {showCol('water') && (<><td data-label="Water Count" className="tnum"><CountLink id={user.id} type="water" value={user.water_count} /></td><td data-label="Last Water">{formatLastActive(user.latest_water_time)}</td></>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserBioVitalsCount;
